/**
 * What a character-creation form is allowed to submit.
 *
 * Split out of the component for the reason every validator should be: the rules are worth
 * asserting without a DOM, and **the numbers in them are the API's, not ours.**
 * `characterDraft.test.ts` reads them straight out of `openapi.json` and fails if these
 * constants drift from the contract — which is the same anti-drift move `pnpm codegen` makes
 * for types, applied to the one part of the contract that generates no type at all. A
 * `maxLength` lives in the schema as a *constraint*, and `openapi-typescript` renders it as
 * `string`; the only way it can reach the client is by being copied, so the only way it can
 * stay correct is by being checked.
 *
 * The failure this prevents is small and infuriating: a player types a 200-character name,
 * presses the button, waits for a round trip and gets a 422 whose message is a Pydantic
 * locator. Nothing here replaces the server's validation — it just means the common case
 * never asks for it.
 */
import type { CharacterSex, StoryCreate } from '@/api/generated';
import type { UiKey } from '@/i18n/en.generated';
import type { TranslateParams } from '@/i18n/t';

/** `StoryCreate.character_name` — `minLength: 1`, `maxLength: 128`. */
export const NAME_MAX = 128;
/** `StoryCreate.character_age` — `minimum: 1`, `maximum: 120`. */
export const AGE_MIN = 1;
export const AGE_MAX = 120;

export const SEXES: readonly CharacterSex[] = ['female', 'male', 'other'];

export interface CharacterDraft {
  name: string;
  sex: CharacterSex;
  /** A string, because it is an `<input>`'s value and "" is a state the number type cannot
   * hold. Parsed once, here, rather than at three call sites. */
  age: string;
  /** `null` means "whatever the profile says" — the API's own default (P7's `StoryCreate`). */
  locale: string | null;
}

/**
 * A key and its parameters, not a sentence. The rules are the same in every language and the
 * wording is not, so this stays a pure function of the draft and the form does the rendering
 * — which also keeps `characterDraft.test.ts` asserting on rules rather than on English.
 */
export interface DraftError {
  key: UiKey;
  params?: TranslateParams;
}

export interface DraftErrors {
  name?: DraftError;
  age?: DraftError;
}

export function emptyDraft(locale: string | null): CharacterDraft {
  // 30 rather than blank: an age field that starts empty reads as required-and-unanswered on
  // a form whose other two questions have answers, and any plausible default is a better
  // starting point to nudge than an empty box.
  return { name: '', sex: 'female', age: '30', locale };
}

export function draftErrors(draft: CharacterDraft): DraftErrors {
  const errors: DraftErrors = {};

  const name = draft.name.trim();
  if (name.length === 0) {
    errors.name = { key: 'ui.character.name_missing' };
  } else if (name.length > NAME_MAX) {
    errors.name = { key: 'ui.character.name_too_long', params: { max: NAME_MAX } };
  }

  const age = Number(draft.age);
  if (draft.age.trim() === '' || !Number.isInteger(age)) {
    errors.age = { key: 'ui.character.age_not_a_number' };
  } else if (age < AGE_MIN || age > AGE_MAX) {
    errors.age = { key: 'ui.character.age_out_of_range', params: { min: AGE_MIN, max: AGE_MAX } };
  }

  return errors;
}

export function isSubmittable(draft: CharacterDraft): boolean {
  return Object.keys(draftErrors(draft)).length === 0;
}

/**
 * The draft as the API takes it. Callers must have checked `isSubmittable` — this trims and
 * parses, it does not validate, and a caller that skips the check sends `NaN`.
 */
export function toStoryCreate(draft: CharacterDraft): StoryCreate {
  return {
    character_name: draft.name.trim(),
    character_sex: draft.sex,
    character_age: Number(draft.age),
    locale: draft.locale,
  };
}
